import React from 'react';
import PropTypes from 'prop-types';
import { LuArrowDownWideNarrow, LuArrowUpWideNarrow, LuChevronDown } from 'react-icons/lu';

const PILL = 'tw-inline-flex tw-h-8 tw-max-w-full tw-items-center tw-overflow-hidden tw-rounded-full tw-border tw-text-xs tw-font-semibold tw-transition-colors tw-duration-200';
const PILL_IDLE = 'tw-border-slate-200 tw-bg-white/60 tw-text-slate-600 dark:tw-border-white/10 dark:tw-bg-white/[0.04] dark:tw-text-slate-300';
const PILL_ACTIVE = 'tw-border-teal-500/40 tw-bg-teal-500/[0.08] tw-text-teal-800 dark:tw-border-teal-400/30 dark:tw-text-teal-200';
const SELECT = 'tw-h-full tw-min-w-0 tw-cursor-pointer tw-appearance-none tw-truncate tw-border-0 tw-bg-transparent tw-pr-6 tw-text-xs tw-font-semibold tw-text-slate-800 tw-shadow-none tw-outline-none focus-visible:tw-underline dark:tw-text-slate-100';

/**
 * A compact select for a data toolbar ("Kategori: Tümü ▾"). It turns teal
 * while its value differs from `idleValue` (e.g. 'all'), so an applied
 * choice is visible at a glance.
 */
const ToolbarSelect = ({ label, value, options, onChange, idleValue, icon: Icon }) => {
  const active = idleValue !== undefined && value !== idleValue;
  return (
    <label className={`${PILL} ${active ? PILL_ACTIVE : PILL_IDLE} tw-relative tw-gap-1 tw-pl-3`}>
      {Icon && <Icon aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />}
      <span className="tw-shrink-0">{label}:</span>
      <select value={value} onChange={(event) => onChange(event.target.value)} className={SELECT}>
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
      <LuChevronDown aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-right-2.5 tw-h-3.5 tw-w-3.5 tw-opacity-60" />
    </label>
  );
};

ToolbarSelect.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string.isRequired, label: PropTypes.string.isRequired })).isRequired,
  onChange: PropTypes.func.isRequired,
  idleValue: PropTypes.string,
  icon: PropTypes.elementType,
};

/** Sort field + direction for a toolbar ("Sırala: Tarih ▾ ⇅"). */
export const SortControl = ({ label, value, options, onChange, ascLabel, descLabel }) => {
  const Icon = value.direction === 'asc' ? LuArrowUpWideNarrow : LuArrowDownWideNarrow;
  const directionLabel = value.direction === 'asc' ? ascLabel : descLabel;
  return (
    <span className={`${PILL} ${PILL_IDLE}`}>
      <label className="tw-relative tw-flex tw-h-full tw-min-w-0 tw-items-center tw-gap-1 tw-pl-3">
        <span className="tw-shrink-0">{label}:</span>
        <select value={value.key} onChange={(event) => onChange({ ...value, key: event.target.value })} className={SELECT}>
          {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        <LuChevronDown aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-right-1.5 tw-h-3.5 tw-w-3.5 tw-opacity-60" />
      </label>
      <button
        type="button"
        onClick={() => onChange({ ...value, direction: value.direction === 'asc' ? 'desc' : 'asc' })}
        title={directionLabel}
        aria-label={directionLabel}
        className="tw-grid tw-h-full tw-w-8 tw-shrink-0 tw-place-items-center tw-border-l tw-border-slate-200 tw-bg-transparent hover:tw-bg-white hover:tw-text-slate-900 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-teal-500/50 dark:tw-border-white/10 dark:hover:tw-bg-white/[0.08] dark:hover:tw-text-white"
      >
        <Icon aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
      </button>
    </span>
  );
};

SortControl.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.shape({ key: PropTypes.string.isRequired, direction: PropTypes.oneOf(['asc', 'desc']).isRequired }).isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string.isRequired, label: PropTypes.string.isRequired })).isRequired,
  onChange: PropTypes.func.isRequired,
  ascLabel: PropTypes.string.isRequired,
  descLabel: PropTypes.string.isRequired,
};

export default ToolbarSelect;
